import { Check, Ruler, AlertTriangle } from "lucide-react";
import { AREA_FIELDS } from "../projects/LocTypeFields";

const up = (s) => String(s || "").toLocaleUpperCase("tr");
const fmt = (n) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n || 0);
const EXTRA_KEYS = AREA_FIELDS.filter(([k]) => k !== "genel_temizlik");

// Hizmet adına göre m² kaynağı: TADİLAT → şube kartındaki Şube m² (tadilat temizliği alanı = şube m²); GENEL TEMİZLİK → genel temizlik m² (+ seçilen ek alanlar)
export function areaMode(service) {
  const n = up(service?.name);
  if (!n) return null;
  if (n.includes("TADİLAT") || n.includes("TADILAT")) return "tadilat";
  if (n.includes("GENEL TEMİZLİK") || n.includes("GENEL TEMIZLIK")) return "genel";
  return null;
}

export function computeAreaQty(location, service, selected = [], manual = {}) {
  const mode = areaMode(service);
  if (!mode || !location || location.branch_type === "ATM") return { mode: null, base: 0, extras: [], total: 0 };
  const area = location.area_m2 || {};
  if (mode === "tadilat") {
    const v = +location.branch_m2 || 0;
    return { mode, base: v, baseLabel: "Şube m²", baseSource: v ? "şube kartı · tadilat temizliği şube m² üzerinden" : "", extras: [], total: v };
  }
  const genel = +area.genel_temizlik || 0;
  const base = genel || (+location.branch_m2 || 0);
  // şube kartında m² yoksa kutucuk yine seçilebilir; m² elle girilir (manual) ve kayıtla birlikte saklanır
  const extras = EXTRA_KEYS.map(([key, label]) => {
    const card = +area[key] || 0;
    return { key, label, card, value: card || +String(manual[key] ?? "").replace(",", ".") || 0, on: selected.includes(key) };
  });
  const total = base + extras.filter((e) => e.on).reduce((s, e) => s + e.value, 0);
  return { mode, base, baseLabel: genel ? "Genel Temizlik m²" : "Şube m²", baseSource: base ? (genel ? "şube kartı · genel temizlik" : "genel temizlik m² tanımsız → şube m² alındı") : "", extras, total };
}

// Veri girişi: m² kaynağı paneli — tadilatta salt bilgi; genel temizlikte tıklanabilir ek alan kutucukları (seçilenler Miktar'a eklenir; kartta m² yoksa elle girilir)
export default function AreaPicker({ area, selected, onChange, manual = {}, onManual, disabled }) {
  if (!area.mode) return null;
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  const on = area.extras.filter((e) => e.on);
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/50 p-3 space-y-2" data-testid="entry-area-picker" data-mode={area.mode}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Ruler className="w-4 h-4 text-amber-700" />
        <span className="font-bold uppercase tracking-wide text-slate-700">{area.baseLabel}</span>
        {area.base
          ? <span className="font-mono font-bold text-slate-900" data-testid="entry-area-base">{fmt(area.base)} m²</span>
          : <span className="inline-flex items-center gap-1 text-red-700 font-semibold" data-testid="entry-area-missing"><AlertTriangle className="w-3.5 h-3.5" /> şube kartında tanımlı değil — Miktarı elle girin</span>}
        {area.baseSource && <span className="text-[10px] text-slate-500">({area.baseSource})</span>}
        {area.mode === "tadilat" && area.base > 0 && <span className="ml-auto text-[10px] text-emerald-700 font-semibold">→ Miktar otomatik doldu (değiştirilebilir)</span>}
      </div>
      {area.mode === "genel" && (
        <>
          <div className="text-[10px] text-slate-500">Ek alan ekle (tıklayın) — seçim yapılmazsa yalnız genel temizlik m² alınır, seçilenlerin m² toplamı eklenir. Şube kartında m² tanımlı değilse seçince m² elle girilir:</div>
          <div className="flex flex-wrap gap-1.5" data-testid="entry-area-chips">
            {area.extras.map((e) => (
              <span key={e.key} className="inline-flex items-stretch">
                <button type="button" disabled={disabled} onClick={() => toggle(e.key)} aria-pressed={e.on} data-testid={`entry-area-${e.key}`}
                  title={e.card ? (e.on ? "Çıkarmak için tıklayın" : "Miktar'a eklemek için tıklayın") : (e.on ? "Çıkarmak için tıklayın" : "Şube kartında m² tanımlı değil — seçince m² elle girilir")}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${e.on && !e.card ? "rounded-l-md" : "rounded-md"} ${e.on ? "bg-emerald-600 border-emerald-700 text-white shadow-sm" : e.card ? "bg-white border-slate-300 text-slate-800 hover:border-amber-500 hover:bg-amber-50" : "bg-white border-dashed border-slate-300 text-slate-600 hover:border-amber-500 hover:bg-amber-50"}`}>
                  <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border ${e.on ? "border-white bg-white" : "border-current"}`}>{e.on && <Check className="w-3 h-3 text-emerald-700" strokeWidth={3} />}</span>
                  {e.label} <span className={`font-mono ${e.on ? "text-white/90" : "text-slate-500"}`}>{e.card ? `${fmt(e.card)} m²` : e.on ? "" : "—"}</span>
                </button>
                {e.on && !e.card && (
                  <span className="inline-flex items-center gap-1 rounded-r-md border border-l-0 border-emerald-700 bg-emerald-50 px-1.5 text-[11px] text-emerald-900">
                    <input type="text" inputMode="decimal" value={manual[e.key] ?? ""} onChange={(ev) => onManual(e.key, ev.target.value)} disabled={disabled} placeholder="m²" autoFocus
                      data-testid={`entry-area-m2-${e.key}`} className="w-16 bg-white border border-emerald-300 rounded px-1.5 py-0.5 text-[11px] font-mono text-slate-900 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                    <span className="font-semibold">m²</span>
                  </span>
                )}
              </span>
            ))}
          </div>
          {on.some((e) => !e.card && !e.value) && <div className="text-[10px] text-red-700 font-semibold" data-testid="entry-area-manual-missing">Seçilen alan için m² girin — boş bırakılırsa Miktar'a eklenmez.</div>}
          <div className="text-xs text-slate-700" data-testid="entry-area-total">
            Miktar = {fmt(area.base)}{on.map((e) => ` + ${fmt(e.value)}`).join("")} = <b className="font-mono text-slate-900">{fmt(area.total)} m²</b>
            {on.length > 0 && <span className="text-[10px] text-slate-500"> ({on.map((e) => e.label).join(", ")})</span>}
            <span className="ml-2 text-[10px] text-emerald-700 font-semibold">→ Miktar alanına yazıldı</span>
          </div>
        </>
      )}
    </div>
  );
}
